import { useEffect, useRef, useState } from 'react';
import { Archive, CircleAlert, Loader, ShieldAlert, Trash2 } from 'lucide-react';
import type { DeletePlan } from '@forq/contracts';

export interface DeleteDialogProps {
  /** Was passieren wird -- null solange geladen wird. */
  readonly plan: DeletePlan | null;
  /** Womit der Datensatz benannt wird: "Kunde", "Maschine", "Vorgang". */
  readonly noun: string;
  /** Wird gezeigt, solange oder falls die Vorschau keinen Namen liefert. */
  readonly fallbackLabel?: string;
  readonly busy?: boolean;
  readonly error?: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * Der Bestätigungsdialog fürs Löschen -- und zwar einer, der SAGT was geschieht.
 *
 * "Wirklich löschen?" ist keine Frage, die man beantworten kann: der Benutzer weiss nicht, ob
 * daran Rechnungen hängen. Deshalb steht hier das Ergebnis VOR dem Klick -- gelöscht oder
 * archiviert, und bei archiviert auch woran es liegt ("3 Vorgänge, 2 Serviceaufträge").
 *
 * Der bestätigende Knopf trägt das Wort, das wirklich passiert. Ein Knopf mit "Löschen", der
 * archiviert, wäre eine Lüge -- und beim nächsten Mal glaubt niemand mehr, was da steht.
 */
export function DeleteDialog({ plan, noun, fallbackLabel, busy = false, error, onConfirm, onCancel }: DeleteDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  // Erst der Abbruch hat den Fokus: Enter soll hier nichts zerstören.
  useEffect(() => { cancelRef.current?.focus(); }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel]);

  const verdict = plan?.verdict;
  const blocked = verdict === 'blocked';
  const archiving = verdict === 'archive';

  return (
    <div
      className="forq-delete__backdrop"
      onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}
    >
      <section className="forq-delete" role="dialog" aria-modal="true" aria-labelledby="forq-delete-title">
        <header className="forq-delete__head">
          <span className={`forq-delete__icon is-${verdict ?? 'loading'}`}>
            {blocked ? <ShieldAlert size={20} /> : archiving ? <Archive size={20} /> : <Trash2 size={20} />}
          </span>
          <div>
            <span className="forq-eyebrow">
              {blocked ? 'Nicht möglich' : archiving ? 'Wird archiviert' : 'Wird gelöscht'}
            </span>
            <h2 id="forq-delete-title">
              {noun}{(plan?.label || fallbackLabel) ? ` ${plan?.label || fallbackLabel}` : ''}
            </h2>
          </div>
        </header>

        {!plan ? (
          <p className="forq-delete__body"><Loader size={14} className="forq-spin" /> Verweise werden geprüft …</p>
        ) : blocked ? (
          <p className="forq-delete__body">{plan.blockedReason}</p>
        ) : archiving ? (
          <>
            <p className="forq-delete__body">
              Dieser Datensatz wird <strong>archiviert</strong>, nicht gelöscht – daran hängt noch etwas.
              Er verschwindet aus den Listen; alles Verknüpfte bleibt unverändert.
            </p>
            <ul className="forq-delete__refs">
              {plan.references.map((reference) => (
                <li key={reference.table}>
                  <strong>{reference.count}</strong> {reference.label}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="forq-delete__body">
            An diesem Datensatz hängt nichts. Er wird <strong>endgültig gelöscht</strong> und lässt
            sich nicht zurückholen.
          </p>
        )}

        {error ? <p className="forq-delete__error" role="alert"><CircleAlert size={13} /> {error}</p> : null}

        <footer className="forq-delete__foot">
          <button type="button" className="forq-delete__cancel" onClick={onCancel} disabled={busy} ref={cancelRef}>
            Abbrechen
          </button>
          {!blocked && (
            <button
              type="button"
              className={archiving ? 'forq-delete__archive' : 'forq-delete__confirm'}
              onClick={onConfirm}
              disabled={busy || !plan}
            >
              {busy ? 'Läuft …' : archiving ? 'Archivieren' : 'Endgültig löschen'}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}

/** Kapselt Laden des Plans, Bestätigen und Fehleranzeige -- damit jedes Modul nur EINEN Zustand hält. */
export function useDeleteDialog(
  loadPlan: () => Promise<DeletePlan>,
  run: () => Promise<void>,
): {
  readonly plan: DeletePlan | null;
  readonly busy: boolean;
  readonly error: string | null;
  readonly confirm: () => void;
} {
  const [plan, setPlan] = useState<DeletePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadPlan()
      .then((loaded) => { if (!cancelled) setPlan(loaded); })
      .catch((cause: unknown) => { if (!cancelled) setError(messageOf(cause)); });
    return () => { cancelled = true; };
    // Bewusst nur einmal beim Öffnen: der Dialog zeigt den Stand von jetzt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    plan,
    busy,
    error,
    confirm: () => {
      setBusy(true);
      setError(null);
      run()
        .catch((cause: unknown) => { setError(messageOf(cause)); })
        .finally(() => setBusy(false));
    },
  };
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Vorgang ist fehlgeschlagen.';
}
